import React, { useCallback, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import Location from "./../../overageEntry/Location";
import { FormikLabeledTextField } from "../../../components/shared/form/FormikLabeledTextField";
import FormikRadioGroupField from "../../../components/shared/form/FormikRadioGroupField";
import FormikUnNaNumberAutocomplete from "../../../components/shared/form/FormikUnNaNumberAutocomplete";
import type { MisdeliveryBolFormValues } from "../../../@hooks/useMisdeliveryBolForm";
import type { FormikProps } from "formik";
import AppState from "../../../AppState";
import { useSelector } from "react-redux";
import { GetPickupDetailsAPI } from "../../../API/getPickupDetails";
import {
  handleKeyDown,
  handleNumberMaxLength,
  handlePaste,
  isEmptyResponse,
} from "../../../helpers";
import NoteBox from "../../../components/noteBox/NoteBox";
import { hasMisdeliveryHazmatApprovalPermissionSelector } from "../../../AppState/selectors/userPermissionsSelectors";
export interface PickupInformationStepProps {
  formik: FormikProps<MisdeliveryBolFormValues>;
  onNext: () => void | Promise<void>;
  formFieldsDisabled?: boolean;
  showUpdateButton?: boolean;
  hideEditButton?: boolean;
  showCancelButton?: boolean;
  onUpdateClick?: () => void;
  onCancelClick?: () => void;
  onGenerateBolClick?: () => void;
  bol?: Partial<MisdeliveryBolFormValues> | null;
  isHazMatPanding?: boolean;
}

/**
 * Pickup Information Step Component
 * Optimized with React.memo to prevent unnecessary re-renders
 * Only re-renders when formik, onNext, fetchPickupDetails, or formFieldsDisabled change
 */
const PickupInformationStepComponent: React.FC<PickupInformationStepProps> = ({
  formik,
  onNext,
  formFieldsDisabled = false,
  showUpdateButton = false,
  hideEditButton = false,
  showCancelButton = false,
  onUpdateClick,
  onCancelClick,
  onGenerateBolClick,
  bol,
  isHazMatPanding,
}) => {
  const hasPickupNumber = Boolean(formik.values.pickupNumber);
  const hasApprovalPermission = useSelector(
    hasMisdeliveryHazmatApprovalPermissionSelector,
  );
  const isFormValueChanged = useSelector(
    (state: AppState) => state.isFormValueChanged,
  );
  const PICKUP_NUMBER_CANNOT_BE_BLANK =
    "Pickup number cannot be blank once entered";
  const PICKUP_NUMBER_INVALID = "Invalid pickup number.";
  const [pickupNumberError, setPickupNumberError] = React.useState<
    string | undefined
  >(undefined);

  // Helper: Populate formik with pickup details
  const populatePickupDetails = useCallback(
    (res: any) => {
      formik.setFieldValue(
        "pickupBusinessName",
        res.pickupBusinessName ?? "",
        true,
      );
      formik.setFieldValue("pickupAddress1", res.pickupAddress1 ?? "", true);
      formik.setFieldValue("pickupAddress2", res.pickupAddress2 ?? "", true);
      formik.setFieldValue("pickupCity", res.pickupCity ?? "", true);
      formik.setFieldValue("pickupState", res.pickupState ?? "", true);
      formik.setFieldValue("pickupZip", res.pickupZip ?? "", true);
      formik.setFieldValue(
        "pickupPhoneNumber",
        res?.pickupPhoneNumber?.toString() ?? "",
        true,
      );
      formik.setFieldValue(
        "pickupContactName",
        res.pickupContactName ?? "",
        true,
      );
      formik.setFieldError("pickupNumber", undefined as any);
      formik.setTouched({
        pickupBusinessName: false,
        pickupAddress1: false,
        pickupAddress2: false,
        pickupCity: false,
        pickupState: false,
        pickupZip: false,
        pickupPhoneNumber: false,
        pickupContactName: false,
        freightDescription: formik.touched.freightDescription,
        isMisdeliveryHazmat: formik.touched.isMisdeliveryHazmat,
        unNumber: formik.touched.unNumber,
        totalPiecesMisdelivered: formik.touched.totalPiecesMisdelivered,
        weight: formik.touched.weight,
      });
    },
    [formik],
  );

  const fetchPickupDetails = useCallback(
    async (pickupNumberRaw: number) => {
      const pickupNumber = (pickupNumberRaw ?? "").toString().trim();

      try {
        const res = await GetPickupDetailsAPI(pickupNumber);

        if (isEmptyResponse(res)) {
          setPickupNumberError(PICKUP_NUMBER_INVALID);
          return;
        }
        setPickupNumberError(undefined);
        populatePickupDetails(res as any);
        return true;
      } catch (error: unknown) {
        console.error("Error fetching pickup details:", error);
        setPickupNumberError(PICKUP_NUMBER_INVALID);
      }
    },
    [populatePickupDetails],
  );

  const { setFieldError, setFieldTouched } = formik;

  // Validate pickup number cannot be cleared if it was saved on server
  useEffect(() => {
    if (!bol?.pickupNumber) return;

    const currentValue = formik.values.pickupNumber;
    const strPickupNumber = (currentValue ?? "").toString().trim();
    const isCleared =
      !currentValue || strPickupNumber === "" || strPickupNumber === "0";

    // Use setTimeout to set error after Formik's validation cycle
    const timeoutId = setTimeout(() => {
      if (isCleared) {
        setFieldTouched("pickupNumber", true, false);
        setFieldError("pickupNumber", PICKUP_NUMBER_CANNOT_BE_BLANK);
      } else {
        setFieldError("pickupNumber", undefined);
      }
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [
    bol?.pickupNumber,
    formik.values.pickupNumber,
    setFieldError,
    setFieldTouched,
  ]);

  // When a Misdelivery Pickup Number is present, pickup-related fields must remain read-only.
  // They are either auto-populated from the pickup lookup, or intentionally locked to prevent drift.
  const pickupDetailsLocked = hasPickupNumber;

  // Check if pickup number validation is failing
  const isPickupNumberInvalid = useCallback(() => {
    if (pickupNumberError) {
      return true;
    }
    return false;
  }, [pickupNumberError]);

  const handlePickupNumberBlur = useCallback(
    async (event: any) => {
      if (formFieldsDisabled) return;
      const rawValue = (event.target.value ?? "").toString().trim();

      // If user cleared the field, don't lookup.
      if (!rawValue || rawValue === "0") {
        if (bol?.pickupNumber) {
          setPickupNumberError(PICKUP_NUMBER_CANNOT_BE_BLANK);
        }
        return;
      }

      const pickupNumber = Number(rawValue);
      if (!Number.isFinite(pickupNumber) || pickupNumber <= 0) return;

      await fetchPickupDetails(pickupNumber);
    },
    [fetchPickupDetails, formFieldsDisabled, bol?.pickupNumber],
  );

  const updateButtonText = () => {
    const isHazmatChangedToYes =
      bol?.isMisdeliveryHazmat !== "Y" &&
      formik.values.isMisdeliveryHazmat === "Y";

    const isHazmatAlreadyYes = bol?.isMisdeliveryHazmat === "Y";

    if (isHazMatPanding) return "Submit & Generate BOL";

    if (
      !hasApprovalPermission &&
      (isHazmatChangedToYes || isHazmatAlreadyYes)
    ) {
      return "Manager Approval";
    }

    return "Update & Regenerate BOL";
  };

  const canShowActionButtons = !isHazMatPanding || hasApprovalPermission;
  const showEdit = showUpdateButton && !hideEditButton;
  const showRegenerate = showUpdateButton && !isHazMatPanding;
  const showCancelAndSubmit = !showUpdateButton && showCancelButton;
  const isSubmitDisabled = !isFormValueChanged || isPickupNumberInvalid();

  const handleNextClick = useCallback(() => {
    if (isPickupNumberInvalid()) return;
    onNext();
  }, [onNext, isPickupNumberInvalid]);

  const handleGenerateBolClick = useCallback(() => {
    if (isPickupNumberInvalid()) return;
    onGenerateBolClick?.();
  }, [onGenerateBolClick, isPickupNumberInvalid]);

  const handleUpdateClick = useCallback(() => {
    if (isPickupNumberInvalid()) return;
    onUpdateClick?.();
  }, [onUpdateClick, isPickupNumberInvalid]);

  return (
    <Box p={0}>
      <Typography className="misdelivery-bol-description">
        For billing purposes, shipment should be Company Business/Non-Revenue
        and consignee is always the pickup terminal’s OS&D Department. This form
        is used to track and document the pickup of freight that SAIA delivered
        in error to a customer, and to facilitate delivery of that product to
        the correct customer. This form is not to be used to return freight to
        the shipper that was shipped in error or has a damage issue.
      </Typography>

      <h3 className="accordion-content-header">Pickup information</h3>

      <Grid
        container
        spacing={2}
        className="filter-margin"
        id="pickup-information"
      >
        <Grid item md={3} xs={12}>
          <FormControl
            fullWidth
            size="medium"
            className="radio-group value-range"
          >
            <FormLabel className="mt-0 mb-10 col-md-12" id="exceptionLocation">
              Misdelivery Pickup Number
            </FormLabel>

            <TextField
              onPaste={handlePaste}
              onKeyDown={handleKeyDown}
              onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleNumberMaxLength(e, 7);
              }}
              autoComplete="off"
              id="pickupNumber"
              placeholder="Enter Misdelivery Pickup Number"
              variant="outlined"
              disabled={formFieldsDisabled}
              onChange={formik.handleChange}
              onBlur={(e) => {
                handlePickupNumberBlur(e);
                formik.handleBlur(e);
              }}
              error={Boolean(pickupNumberError)}
              data-testid="misdeliveryPickupNumber"
              name="pickupNumber"
              type="number"
              value={formik.values.pickupNumber}
            />
          </FormControl>
          {pickupNumberError && (
            <span className={"form-error"}>{pickupNumberError}</span>
          )}
        </Grid>

        <FormikLabeledTextField
          formik={formik}
          name="pickupBusinessName"
          label="Business Name"
          placeholder="Enter Business Name"
          required
          onBlur={formik.handleBlur}
          disabled={pickupDetailsLocked || formFieldsDisabled}
          maxLength={30}
          gridProps={{ md: 3, xs: 12 }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="pickupAddress1"
          label="Address 1"
          placeholder="Enter Address 1"
          required
          disabled={pickupDetailsLocked || formFieldsDisabled}
          maxLength={30}
          gridProps={{ md: 3, xs: 12 }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="pickupAddress2"
          label="Address 2"
          placeholder="Enter Address 2"
          disabled={pickupDetailsLocked || formFieldsDisabled}
          maxLength={30}
          gridProps={{ md: 3, xs: 12 }}
        />

        <Location
          osdDetails={bol}
          formFieldsDisabled={pickupDetailsLocked || formFieldsDisabled}
          formik={formik}
          key={formik.values.pickupState} // Reset internal state when pickup number changes
          required
          formObject={{
            cityField: "pickupCity",
            zipField: "pickupZip",
            cityLabel: "City",
            zipLabel: "Zip",
            stateField: "pickupState",
            stateLabel: "State",
          }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="pickupPhoneNumber"
          label="Phone Number"
          placeholder="Enter Phone Number"
          disabled={pickupDetailsLocked || formFieldsDisabled}
          type="number"
          maxLength={10}
          gridProps={{ md: 3, xs: 12 }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="pickupContactName"
          label="Contact Name(First and Last)"
          placeholder="Enter Contact Name"
          disabled={pickupDetailsLocked || formFieldsDisabled}
          maxLength={30}
          gridProps={{ md: 3, xs: 12 }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="freightDescription"
          label="Description of freight to be picked up"
          placeholder="Enter description of freight"
          required
          maxLength={35}
          gridProps={{ md: 9, xs: 12 }}
          disabled={formFieldsDisabled}
        />

        <FormikRadioGroupField
          formik={formik}
          name="isMisdeliveryHazmat"
          label="Is this HazMat?"
          required
          options={[
            { value: "Y", label: "Yes" },
            { value: "N", label: "No" },
          ]}
          gridProps={{ md: 3, xs: 12 }}
          disabled={formFieldsDisabled}
        />
        {formik?.values?.isMisdeliveryHazmat !== "Y" && (
          <Grid item md={3} xs={12} />
        )}
        {formik?.values?.isMisdeliveryHazmat === "Y" && (
          <FormikUnNaNumberAutocomplete
            formik={formik}
            name="unNumber"
            unNumberSequence="unNumberSequence"
            label="UN/NA#"
            placeholder="Search UN/NA#"
            required
            gridProps={{ md: 3, xs: 12 }}
            disabled={formFieldsDisabled}
            minSearchLength={3}
            debounceMs={500}
            page={1}
            limit={10}
          />
        )}

        <FormikLabeledTextField
          formik={formik}
          name="totalPiecesMisdelivered"
          label="Number of skids/Pieces"
          placeholder="Enter number of skids/pieces"
          required
          type="number"
          maxLength={5}
          gridProps={{ md: 3, xs: 12 }}
          disabled={formFieldsDisabled}
        />

        <FormikLabeledTextField
          formik={formik}
          name="weight"
          label="Weight"
          placeholder="Enter weight"
          required
          type="number"
          maxLength={6}
          gridProps={{ md: 3, xs: 12 }}
          disabled={formFieldsDisabled}
        />

        {formik?.values?.isMisdeliveryHazmat === "Y" && (
          <>
            <Grid item md={6} xs={12}>
              <NoteBox message="Hazmat BOL needs to be sent to Carissa Atkinson (catkinson@saia.com) for approval before sending to the customer." />
            </Grid>
            <Grid item md={6} xs={12}>
              {" "}
            </Grid>
          </>
        )}
        <FormikLabeledTextField
          formik={formik}
          name="proNumber"
          label="Misdelivered on PRO (Master PRO)"
          type="number"
          disabled
          maxLength={11}
          gridProps={{ md: 3, xs: 12 }}
        />

        <FormikLabeledTextField
          formik={formik}
          name="misdeliveredPalletPro"
          label="Applied to PRO"
          disabled
          type="number"
          maxLength={11}
          gridProps={{ md: 3, xs: 12 }}
        />
      </Grid>

      <Grid container spacing={2} className="filter-margin">
        <Grid item xs={12} className="btn-group-submit">
          {canShowActionButtons && (
            <>
              {showEdit && (
                <Button
                  type="button"
                  variant="contained"
                  className="assign ml"
                  data-testid="misdelivery-bol-update-button"
                  onClick={handleUpdateClick}
                >
                  Edit
                </Button>
              )}

              {showRegenerate && (
                <Button
                  type="button"
                  variant="contained"
                  className="entry ml"
                  data-testid="misdelivery-bol-regenerate-button"
                  onClick={handleGenerateBolClick}
                >
                  Regenerate BOL
                </Button>
              )}

              {showCancelAndSubmit && (
                <>
                  <Button
                    type="button"
                    variant="contained"
                    className="clear-btn ml"
                    data-testid="misdelivery-bol-cancel-button"
                    onClick={() => {
                      onCancelClick?.();
                      setPickupNumberError(undefined);
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    className={`entry ml ${
                      isSubmitDisabled ? "disable-btn disable" : ""
                    }`}
                    data-testid="misdelivery-bol-regenerate-button"
                  >
                    {updateButtonText()}
                  </Button>
                </>
              )}
            </>
          )}

          {isHazMatPanding && hasApprovalPermission && formFieldsDisabled && (
            <Button type="submit" variant="contained" className="entry ml">
              Submit & Generate BOL
            </Button>
          )}

          <Button
            type="button"
            variant="contained"
            className="entry ml"
            onClick={handleNextClick}
          >
            Next
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

const PickupInformationStep = React.memo(PickupInformationStepComponent);
export default PickupInformationStep;
